import { Activity, BarChart3, CalendarDays, Clapperboard, Compass, Gauge, ListVideo, Menu, Orbit, Settings, Sparkles, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { agentAuthToken, api } from "./api";
import channelAvatar from "../../../branding/exports/avatar_800x800.png";

const navigation = [
  ["Overview", "/", Compass], ["Pipeline", "/pipeline", Gauge], ["Create", "/create", Sparkles],
  ["Schedule", "/schedule", CalendarDays], ["Content", "/content", ListVideo], ["Analytics", "/analytics", BarChart3],
  ["Milestones", "/milestones", Orbit], ["Activity", "/activity", Activity], ["Settings", "/settings", Settings]
] as const;

export function Layout() {
  const [open, setOpen] = useState(false);
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => api<Record<string, unknown>>("/v1/settings") });
  const connectivity = useQuery({ queryKey: ["overview"], queryFn: () => api<any>("/v1/overview"), refetchInterval: 15_000 });
  const values = settings.data?.data ?? {};
  const branded = values["branding.enabled"] !== false;
  const channelName = String(values["branding.channel_name"] ?? "FIELD NOTES");
  const channelTagline = String(values["branding.tagline"] ?? "CONTROL CENTER");
  const configuredAvatar = values["branding.avatar_url"];
  const avatar = configuredAvatar ? String(configuredAvatar) : channelAvatar;
  const agentOnline = connectivity.data?.source === "live";
  const accent = String(values["ui.accent"] ?? "signal");
  useEffect(() => {
    const palettes: Record<string, { accent: string; accentDark: string }> = {
      signal: { accent: "#f36b3d", accentDark: "#8e3921" },
      horizon: { accent: "#4997d0", accentDark: "#245d88" },
      nebula: { accent: "#8f78d4", accentDark: "#55428f" },
      aurora: { accent: "#36a58d", accentDark: "#1f6d60" },
    };
    const selected = palettes[accent] ?? palettes.signal;
    document.documentElement.style.setProperty("--accent", selected.accent);
    document.documentElement.style.setProperty("--accent-dark", selected.accentDark);
  }, [accent]);
  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") void Notification.requestPermission();
    let socket: WebSocket | undefined;
    void agentAuthToken().then(token => {
      const cursor = localStorage.getItem("control-center-event-cursor") ?? "0";
      const desktop = location.hostname === "tauri.localhost";
      const protocol = desktop || location.protocol !== "https:" ? "ws" : "wss";
      const host = desktop ? "127.0.0.1:8765" : location.host;
      socket = new WebSocket(`${protocol}://${host}/v1/events?after=${cursor}&token=${encodeURIComponent(token)}`);
      socket.onmessage = event => {
        const payload = JSON.parse(event.data);
        if (payload.id) localStorage.setItem("control-center-event-cursor", String(payload.id));
        if (Notification.permission === "granted" && ["job.completed", "job.failed", "milestone.achieved"].includes(payload.event_type)) {
          new Notification("Field Notes Control Center", { body: payload.message });
        }
      };
    });
    return () => socket?.close();
  }, []);
  return <div className="shell">
    <aside className={`sidebar ${open ? "open" : ""}`}>
      <div className="brand"><div className={`brand-mark ${branded ? "channel-avatar" : ""}`}>{branded ? <img src={avatar} alt={`${channelName} channel profile`} /> : <Clapperboard />}</div><div><strong>{branded ? channelName : "CONTROL CENTER"}</strong><span>{branded ? channelTagline : "PIPELINE AUTOMATION"}</span></div><button className="icon-button mobile-only" onClick={() => setOpen(false)} aria-label="Close navigation"><X /></button></div>
      <nav>{navigation.map(([label, path, Icon]) => <NavLink key={path} to={path} end={path === "/"} onClick={() => setOpen(false)}><Icon /><span>{label}</span></NavLink>)}</nav>
      <div className="sidebar-foot"><span className={`signal ${agentOnline ? "" : "offline"}`} /><div><strong>Pipeline PC</strong><small>{connectivity.isLoading ? "Checking connection…" : agentOnline ? "Secure local agent" : "Offline · cached data"}</small></div></div>
    </aside>
    <main><div className="mobile-top"><button className="icon-button" onClick={() => setOpen(true)} aria-label="Open navigation"><Menu /></button><strong>{branded ? channelName : "CONTROL CENTER"}</strong><span className={`signal ${agentOnline ? "" : "offline"}`} /></div><Outlet /></main>
    <div className={`scrim ${open ? "show" : ""}`} onClick={() => setOpen(false)} />
    <nav className="mobile-nav">{navigation.slice(0, 4).map(([label, path, Icon]) => <NavLink key={path} to={path} end={path === "/"}><Icon /><span>{label}</span></NavLink>)}<button onClick={() => setOpen(true)}><Menu /><span>More</span></button></nav>
  </div>;
}
